export type ContainerTarget = string | HTMLElement | { current: HTMLElement } | null | undefined
export type ContainerExpander = {
  get expanded(): boolean
  expand(inset: ContainerTarget, options?: { margin?: number; height?: number; watch?: boolean }): () => void
  unexpand(inset: ContainerTarget): boolean
  adjustContainer(): void
  get containerHeightFreezed(): boolean
  freezeContainerHeight(options?: { overflow?: string }): boolean
  unfreezeContainerHeight(): boolean
}

const resizeObserversMap = new WeakMap<HTMLElement, ResizeObserver>()

function getNode(target: ContainerTarget): HTMLElement | undefined {
  switch (true) {
    // selector
    case 'string' === typeof target: {
      return document.querySelector(target)
    }
    // element
    case target instanceof HTMLElement: {
      return target
    }
    // ref
    case null != target &&
      'object' === typeof target &&
      (target as { current: HTMLElement }).current instanceof HTMLElement: {
      return target.current
    }
    default: {
      return void 0
    }
  }
}

export default function containerExpander(
  container: ContainerTarget,
  { margin: defaultMargin = 0, watch: defaultWatch = false } = {}
): ContainerExpander {
  const containerProps: {
    expanded: boolean
    containerHeightFreezed: boolean
    node: HTMLElement | undefined
    originalMinHeight: string | undefined
    originalHeight: string | undefined
    originalOverflow: string | undefined
  } = {
    expanded: false,
    containerHeightFreezed: false,
    node: void 0,
    originalMinHeight: void 0,
    originalHeight: void 0,
    originalOverflow: void 0
  }

  const insets = new Map<HTMLElement, { margin: number; height: number }>()

  const initContainer = () => {
    if (null == containerProps.node || !document.contains(containerProps.node)) {
      containerProps.node = getNode(container)
      containerProps.originalMinHeight = null == containerProps.node ? void 0 : containerProps.node.style.minHeight
      containerProps.originalHeight = null == containerProps.node ? void 0 : containerProps.node.style.height
    }
  }

  const adjustContainer = () => {
    initContainer()
    if (null != containerProps.node) {
      if (0 === insets.size) {
        // revert to original minHeight
        containerProps.node.style.minHeight = containerProps.originalMinHeight
        containerProps.expanded = false
      } else {
        // get insets lowest bottom
        let lowestInsetBottom = 0
        insets.forEach(({ margin = 0, height }, inset) => {
          const insetRect = inset.getBoundingClientRect()
          const insetBottom = ('number' === typeof height ? insetRect.top + height : insetRect.bottom) + margin
          if (insetBottom > lowestInsetBottom) {
            lowestInsetBottom = insetBottom
          }
        })
        // set container minHeights
        const { top: containerTop } = containerProps.node.getBoundingClientRect()
        containerProps.node.style.minHeight = `${lowestInsetBottom - containerTop}px`
        containerProps.expanded = true
      }
    }
  }

  const expandContainerAPI: ContainerExpander = {
    get expanded() {
      return containerProps.expanded
    },
    expand(inset, { margin = defaultMargin, height, watch = defaultWatch } = {}) {
      const insetNode = getNode(inset)
      if (null != insetNode) {
        insets.set(insetNode, { margin, height })
        if (watch) {
          const resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(adjustContainer)
          })
          resizeObserver.observe(insetNode)
          resizeObserversMap.set(insetNode, resizeObserver)
        }
        adjustContainer()
      }
      return () => expandContainerAPI.unexpand(inset)
    },
    unexpand(inset) {
      const insetNode = getNode(inset)
      if (null != insetNode) {
        insets.delete(insetNode)
        const resizeObserver = resizeObserversMap.get(insetNode)
        if (resizeObserver) {
          resizeObserver.unobserve(insetNode)
          resizeObserversMap.delete(insetNode)
        }
        adjustContainer()
        return true
      }
      return false
    },
    adjustContainer() {
      if (containerProps.expanded) {
        adjustContainer()
      }
    },
    get containerHeightFreezed() {
      return containerProps.containerHeightFreezed
    },
    freezeContainerHeight({ overflow } = {}) {
      if (!containerProps.containerHeightFreezed) {
        initContainer()
        if (null != containerProps.node) {
          if (overflow) {
            containerProps.originalOverflow = containerProps.node.style.overflow
            containerProps.node.style.overflow = overflow
          }
          const { top, bottom } = containerProps.node.getBoundingClientRect()
          containerProps.node.style.height = `${bottom - top}px`
          containerProps.containerHeightFreezed = true
          return true
        }
      }
      return false
    },
    unfreezeContainerHeight() {
      if (containerProps.containerHeightFreezed && null != containerProps.node) {
        containerProps.node.style.overflow = containerProps.originalOverflow
        containerProps.node.style.height = containerProps.originalHeight
        containerProps.containerHeightFreezed = false
        return true
      }
      return false
    }
  }

  return expandContainerAPI
}
